import { getChatLastMessageTimeString } from "../utils";
import {
    DefaultGroupIcon,
    DefaultUserIcon,
    FileUploadValidationResponseV2,
    INDICATOR_STATUS_STRINGS,
    IndicatorStatus,
    UserStatus
} from "./conversation.model";

export class Pageable {
    sort: Sort;
    offset: number;
    pageNumber: number;
    pageSize: number;
    paged: boolean;
    unpaged: boolean;
}

export class Sort {
    sorted: boolean;
    unsorted: boolean;
    empty: boolean;
}

export class PaginationDetails {
    pageable: Pageable;
    last: boolean;
    totalElements: number;
    totalPages: number;
    number: number;
    sort: Sort;
    size: number;
    first: boolean;
    numberOfElements: number;
    empty: boolean;
}

export class GetChatMessageResponse<T> {
    content: T[];
    pageable: Pageable;
    last: boolean;
    totalElements: number;
    totalPages: number;
    number: number;
    sort: Sort;
    size: number;
    first: boolean;
    numberOfElements: number;
    empty: boolean;
}

export interface FileMetadata {
    attachmentId: number;
    fileName: string;
    fileSize: number;
    thumbnailContent?: string;  // Base64 encoded thumbnail image (legacy DB rows only)
    /** Presigned S3 thumbnail (PT-16085 F8); S3 rows have no base64 content anymore. */
    thumbnailUrl?: string;
    messageId?: number;
}

/** One entry of GET /attachments/download-thumbnails (PT-16085 F8). */
export interface AttachmentThumbnailResponse {
    messageAttachmentId: number;
    fileType: string;
    /** Presigned S3 thumbnail; null for legacy rows or types without a preview. */
    thumbnailUrl: string | null;
    /** Base64 thumbnail of a legacy DB row; null for S3 rows. */
    fileContent: string | null;
}

export class ChatMessage {
    messageId: number;
    senderId: number;
    receiverId: number;
    content: string;
    replyId: number;
    isDelivered: boolean;
    isRead: boolean;
    isEdited: boolean;
    isDeleted: boolean;
    timestamp: string;
    groupId: number;
    taskAttachmentId: number;
    reply: ChatMessage;
    selected: boolean = false;
    messageType: string | null;

    //Attachments related fields
    messageAttachmentIds: string; // i.e "1,2",
    fileMetadataList: FileMetadata[] = []; // List of file metadata objects

    // fields for frontend use
    sender: ChatUser | undefined;
    receiver: ChatUser | undefined;
    isReply: boolean = false;
    isYou: boolean = false;
    tickStatus: string;
    readCount: number;
    deliverCount: number;
    isStarred?: boolean;
    isPinned?: boolean | null;

    constructor(partialMessage: Partial<ChatMessage>, chatUsers: ChatUser[], userAllAccountIds: number[]) {
        const sender = chatUsers?.find((user) => user.accountId === partialMessage.senderId);
        Object.assign(this, partialMessage);
        this.sender = sender;
        this.receiver = chatUsers?.find((user) => user.accountId === this.receiverId);
        this.isReply = this.replyId !== null && this.replyId !== undefined;
        this.isYou = userAllAccountIds.includes(this.senderId);
        this.fileMetadataList = partialMessage.fileMetadataList || [];
        if (partialMessage.reply) {
            const replySender = chatUsers.find((user) => user.accountId === partialMessage.reply?.senderId);
            if (replySender) {
                this.reply = new ChatMessage(partialMessage.reply, chatUsers, userAllAccountIds);
            }
        }
    }
}

export class SendMessageAction {
    content: string;
    action: MessageActionEnum;
    receiverId: number | null;
    replyId: number;
    messageId: number;
    senderId: number;
    groupId: number;
    attachmentIdList: number[] = []; // List of attachment IDs
}

export enum MessageActionEnum {
    NEW = "NEW",
    EDIT = "EDIT",
    DELETE = "DELETE",
    TAG = "TAG",
    GROUP = "GROUP",
    REPLY = "REPLY",
}

export enum WebSocketMessageType {
    PING = "ping",
    PONG = "pong",
    MESSAGE = "message",
    CONNECTION = "connection",
    DISCONNECT = "disconnect",
}

export enum MessageType {
    SYSTEM = "SYSTEM",
}

export class ChatUser {
    accountId: number;
    orgId: number;
    isActive: boolean;
    email: string;
    userId: number;
    firstName: string;
    lastName: string;
    isDeleted?: boolean;
    isAdmin: boolean;

    //     fields for frontend use
    selected: boolean = false;
    profileImage: string = "";
}

export class GroupMember {
    accountId: number;
    email: string;
    firstName: string;
    lastName: string;
    isActive: boolean;
    isAdmin: boolean;
    isDeleted: boolean;
    orgId: number;
    userId: number;

    // fields for frontend use
    profileImage: string = "";
}

export enum MessageContextMenuAction {
    EDIT = "Edit",
    DELETE = "Delete",
    REPLY = "Reply",
    COPY = "Copy",
    PIN = "Pin",
    UNPIN = "Unpin",
}

export enum WebSocketConnectionStatus {
    CONNECTING = "connecting",
    CONNECTED = "connected",
    ERROR = "error",
    CLOSED = "closed",
    DISCONNECTED = "disconnected",
    RECONNECTING = "reconnecting",
}

export enum ConversationSideMenu {
    ALL_MESSAGES = "All Messages",
    DIRECT_MESSAGES = "Direct Messages",
    GROUPS = "Groups",
    FAVORITES = "Favorites",
}

export interface ConversationSideMenuList {
    title: ConversationSideMenu;
    icon: string;
    count: number;
    isActive: boolean;
    isWorking: boolean;
}

export const conversationSideMenuList: ConversationSideMenuList[] = [
    {
        title: ConversationSideMenu.ALL_MESSAGES,
        icon: "message",
        count: 0,
        isActive: true,
        isWorking: true,
    },
    {
        title: ConversationSideMenu.DIRECT_MESSAGES,
        icon: "chat",
        count: 0,
        isActive: false,
        isWorking: true,
    },
    {
        title: ConversationSideMenu.GROUPS,
        icon: "group",
        count: 0,
        isActive: false,
        isWorking: true,
    },
    {
        title: ConversationSideMenu.FAVORITES,
        icon: "star",
        count: 0,
        isActive: false,
        isWorking: false,
    },
];

export class Group {
    groupId: number;
    name: string;
    type: string;
    users: GroupMember[];
    orgId: number;
    entityId: number;
    entityTypeId: number;
    admins: GroupMember[];
    groupIconColor: string;
    groupIconCode: string;
    description: string;
    createdByUser: ChatUser;
    isActive: boolean;

    // frontend use only.
    selected: boolean;
    recentMessage: string;
    recentMessageTime: string = "10:00 AM";
    unreadCount: number; // number of unread messages
}

export enum FetchMessagesBasedOnSelectedTab {
    ALL_MESSAGES = 0,
    DIRECT_MESSAGES = 1,
    GROUPS = 2,
    FAVORITES = 3,
}

export enum ChatEntityType {
    PERSONAL_MESSAGE = 1,
    CUSTOM_GROUP = 2,
    SYSTEM_ORG = 3,
    SYSTEM_BU = 4,
    SYSTEM_PROJ = 5,
    SYSTEM_TEAM = 6,
}

export enum ChatEntityTypeString {
    CUSTOM_GROUP = "CUSTOM",
}

/**
 * Maps a Group coming from the backend to the ChatEntityType used by recent chats.
 * Custom groups are CUSTOM_GROUP; system groups are derived from the entity level they belong to
 * (Constants.EntityTypes: ORG = 2, BU = 3, PROJECT = 4, TEAM = 5).
 */
export function toChatEntityType(group: Pick<Group, "type" | "entityTypeId">): ChatEntityType {
    if (group.type === ChatEntityTypeString.CUSTOM_GROUP) {
        return ChatEntityType.CUSTOM_GROUP;
    }
    switch (Number(group.entityTypeId)) {
        case 3:
            return ChatEntityType.SYSTEM_BU;
        case 4:
            return ChatEntityType.SYSTEM_PROJ;
        case 5:
            return ChatEntityType.SYSTEM_TEAM;
        default:
            return ChatEntityType.SYSTEM_ORG;
    }
}

export class CommonRecentChat {
    entityName: string;
    entityType: ChatEntityType;
    entityId: number;
    isActive: boolean;
    lastMessage: string | null;
    lastMessageSenderAccountId: number | null;
    lastMessageId: number | null;
    lastMessageTimestamp: string | null;
    user: ChatUser;

    // frontend use only.
    selected: boolean;
    isYou: boolean;
    lastMessageSenderName: string;
    groupObject: Group | null;
    lastMessageTimeString: string;
    icon_url: string;
    isGroup: boolean;
    count: number;
    orgId: number;
    orgName: string;
    profilePictureUrl: string;
    userIsNotActive: boolean = false;
    userStatus: UserStatus;
    unreadMessageCount: number;
    lastMessageSenderIsCurrentUser: boolean; // to check if last message sender is current user (Personal and Group chats)
    pinnedMessages?: string

    constructor(partialChat: Partial<CommonRecentChat>, currentUserId: number[], userStaus?: UserStatus) {
        Object.assign(this, partialChat);
        this.isGroup = this.entityType !== ChatEntityType.PERSONAL_MESSAGE;
        this.isYou = this.entityType == ChatEntityType.PERSONAL_MESSAGE && currentUserId.includes(this.entityId);
        this.lastMessageTimeString = getChatLastMessageTimeString(this.lastMessageTimestamp);
        this.icon_url = this.isGroup ? DefaultGroupIcon : DefaultUserIcon;
        this.lastMessageSenderIsCurrentUser = this.lastMessageSenderAccountId
            ? currentUserId?.includes(this.lastMessageSenderAccountId)
            : false;
        this.lastMessageSenderName = this.lastMessageSenderAccountId
            ? currentUserId?.includes(this.lastMessageSenderAccountId)
                ? "You"
                : this.groupObject?.users.find((user) => user.accountId === this.lastMessageSenderAccountId)
                      ?.firstName || ""
            : "";
        this.userIsNotActive = !this.isActive;
        if( userStaus ) {
            this.userStatus = userStaus;
        } else if(!this.isGroup && this.user && this.user.userId) {
            this.userStatus = new UserStatus(
                this.user.userId,
                IndicatorStatus.OFFLINE,
                INDICATOR_STATUS_STRINGS[IndicatorStatus.OFFLINE],
                INDICATOR_STATUS_STRINGS[IndicatorStatus.OFFLINE],
            )
        }
    }
}

export class CreateGroupPayload {
    name: string;
    type: ChatEntityTypeString.CUSTOM_GROUP;
    orgId: number;
    entityId: number;
    entityTypeId: number;
    users: number[];
    groupIconCode: string;
    groupIconColor: string;
    description: string;
}
export class CreatedByUser {
    userId: number;
    userName: string;
    userEmail: string;
}

export class UpdateGroupPayload {
    groupId: number;
    name: string;
    description: string;
    groupIconCode: string; // hash (icon name)
    groupIconColor: string; // #FFFFFF
    orgId: number;
    users: number[];
    type: ChatEntityTypeString.CUSTOM_GROUP;
    entityId: number;
    entityTypeId: number;
    isActive: boolean;
    createdByUser: CreatedByUser;
    createdDate: string;
}

export class AddRemoveUserToGroupPayload {
    groupId: number;
    userId: number;
}

export class BulkAddRemoveUserToGroupPayload {
    groupId: number;
    userIds: number[];
}

// File/Attachment related types
export interface AttachmentUploadMessage {
    receiverId: number | null; // null for group messages
    senderId: number;
    groupId?: number | null; // null for direct messages
}

export interface AttachmentUploadRequest {
    files: File[];
    uploaderAccountId: number;
    message: AttachmentUploadMessage;
}

export interface AttachmentUploadSuccess {
    messageAttachmentId: number;
    fileFullName: string;
    fileSize: number;
}

export interface AttachmentUploadFail {
    fileName: string;
    error: string;
    code?: string;
}

export interface AttachmentUploadResponse {
    successListIds: number[];
    fail: AttachmentUploadFail[];
    success: AttachmentUploadSuccess[];
}

export type AttachmentUploadStatus = "idle" | "uploading" | "success" | "error";

export const AttachmentUploadStatusConst = {
    IDLE: "idle" as AttachmentUploadStatus,
    UPLOADING: "uploading" as AttachmentUploadStatus,
    SUCCESS: "success" as AttachmentUploadStatus,
    ERROR: "error" as AttachmentUploadStatus,
};

export interface AttachmentUploadState {
    status: AttachmentUploadStatus;
    progress: number;
    response?: AttachmentUploadResponse;
    error?: string;
    validationResponse?: FileUploadValidationResponseV2;
}

export interface FileValidationError {
    file: File;
    errorType: FileValidationErrorType;
    errorMessage: string;
}

export enum FileValidationErrorType {
    UNSUPPORTED_TYPE = "UNSUPPORTED_TYPE",
    FILE_TOO_LARGE = "FILE_TOO_LARGE",
    FILENAME_TOO_LONG = "FILENAME_TOO_LONG",
    DUPLICATE_NAME = "DUPLICATE_NAME",
    EMPTY_FILE = "EMPTY_FILE",
    NO_EXTENSION = "NO_EXTENSION",
    INVALID_CHARACTERS = "INVALID_CHARACTERS",
    EXCEEDING_COUNT_LIMIT = "EXCEEDING_COUNT_LIMIT",
}

export class ChatMessagesGroupedByDate {
    date: string; // e.g., "2023-10-01"
    displayDate?: string; // formatted for display, e.g., "Today", "Yesterday", "Oct 1"
    messages: ChatMessage[];

    constructor(date: string, messages: ChatMessage[], displayDate?: string) {
        this.date = date;
        this.messages = messages;
        this.displayDate = displayDate;
    }
}
export interface Range {
    index: number;
    length: number;
}
export type Context = Record<string, any>;

export interface KeyBinding {
    // Defines the key to trigger the binding
    key: number | string;

    // Optional modifier keys
    shiftKey?: boolean;
    format?: Record<string, any> | string[];
}

export interface KeyboardModule {
    addBinding: (
        keyBinding: KeyBinding,
        handler: (range: Range, context: Context) => boolean,
    ) => void;
}

export const KEY_MAP = {
    ARROW_DOWN: 'ArrowDown',
    ARROW_UP: 'ArrowUp',
    ENTER: 'Enter',
    ESCAPE: 'Escape',
};

export const MENTION_TRIGGER = '@';

export const ACK_MESSAGE_ACTION = {
    DELIVERY_ACK: 'DELIVERY_ACK',
    READ_ACK: 'READ_ACK',
}

export interface MessageGap {
    upperBoundMessageId: number;
    lowerBoundMessageId: number;
}

export interface StarredMessage {
    starId: number;
    accountId: number;
    messageId: number;
    chatEntityType: ChatEntityType | null;
    chatEntityId: number;
    updatedAt: string;
    starredAt: string;
    message: ChatMessage;
    active: boolean;
}

export enum PinDurationId {
    TWELVE_HOURS = 1,
    TWENTY_FOUR_HOURS = 2,
    SEVEN_DAYS = 3,
    THIRTY_DAYS = 4,
}

export interface PinDurationOption {
    id: PinDurationId;
    label: string;
}

export const PIN_DURATION_OPTIONS: PinDurationOption[] = [
    { id: PinDurationId.TWELVE_HOURS, label: '12 hours' },
    { id: PinDurationId.TWENTY_FOUR_HOURS, label: '24 hours' },
    { id: PinDurationId.SEVEN_DAYS, label: '7 days' },
    { id: PinDurationId.THIRTY_DAYS, label: '30 days' },
];

export const PIN_DURATION_DIALOG_TEXT = {
    TITLE: 'Set pin duration',
    SUBTITLE: 'You can unpin at any time.',
    CANCEL: 'Cancel',
    CONFIRM: 'Pin',
} as const;

export interface PinMessagePayload {
    action: 'PIN_MESSAGE';
    messageId: number;
    senderId: number;
    receiverId: number | null;
    groupId: number | null;
    pinnedDurationId: PinDurationId;
    isDeleted?: boolean;
}

export interface SavedMessageResponse {
    pinId: number | null;
    pinnedBy: number | null;
    pinExpirationTime: string | null;
    pinnedAt: string | null;
    starId: number | null;
    starredAt: string | null;
    chatEntityType: number;
    chatEntityId: number;
    message: ChatMessage;
}

export interface GetSavedMessagesPayload {
    accountId: number;
    type: 'PINNED' | 'STARRED' | 'BOTH';
    chatEntityType: number;   // 0=All, 1=DM, 2=Group
    chatEntityId: number | null;
}
